import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../../db/local.js';
import { readProfile, updateWater } from '../../db/profile.js';
import { toNumber } from './FoodForm.js';

/**
 * Wasser einstellen (18.10.2026): Tagesziel (Vorgabe 1,5 l nach DGE),
 * Mittagsfrage (ab Werk an) und Nachfragen bei Rückstand (ab Werk aus).
 */
export function WaterSetting(): React.JSX.Element | null {
  const { t } = useTranslation();
  const water = useLiveQuery(async () => (await readProfile(db)).water, []);
  if (!water) return null;

  return (
    <section aria-labelledby="settings-water" className="kw-tile flex flex-col gap-3">
      <h4 id="settings-water" className="kw-kicker">
        {t('water.settingsHeading')}
      </h4>
      <WaterTargetField />
      <button
        type="button"
        aria-pressed={water.midday}
        onClick={() => void updateWater({ midday: !water.midday }, db)}
        className="kw-toggle self-start"
      >
        {t('water.midday')}
      </button>
      <button
        type="button"
        aria-pressed={water.pace}
        onClick={() => void updateWater({ pace: !water.pace }, db)}
        className="kw-toggle self-start"
      >
        {t('water.pace')}
      </button>
      <p className="text-xs text-ink-muted">{t('water.remindersWhy')}</p>
    </section>
  );
}

/**
 * Das Tagesziel in ml — in den Einstellungen und in der Wasser-Kachel
 * dasselbe Feld. Gespeichert beim Verlassen; Unsinn springt zurück.
 */
export function WaterTargetField({ onSaved }: { onSaved?: () => void }): React.JSX.Element | null {
  const { t } = useTranslation();
  const targetMl = useLiveQuery(async () => (await readProfile(db)).water.targetMl, []);
  const [target, setTarget] = useState('');
  const id = useId();
  useEffect(() => {
    if (targetMl !== undefined) setTarget(String(targetMl));
  }, [targetMl]);
  if (targetMl === undefined) return null;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="kw-label flex w-40 flex-col gap-1.5">
        {t('water.target')}
        <input
          id={id}
          inputMode="numeric"
          value={target}
          onChange={(event) => setTarget(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') event.currentTarget.blur();
          }}
          onBlur={() => {
            const ml = toNumber(target);
            if (ml !== null && ml >= 250 && ml <= 10_000) {
              if (ml !== targetMl) void updateWater({ targetMl: ml }, db);
              onSaved?.();
            } else setTarget(String(targetMl));
          }}
          className="kw-field"
        />
      </label>
      <p className="text-xs text-ink-muted">{t('water.targetSource')}</p>
    </div>
  );
}
